import { Grid } from '@mui/material'
import {
    type GetCandidateProfileForRecruiterQuery,
    type GetCandidateProfileForRecruiterQueryVariables,
} from '__generated__/graphql'
import { type DataGridProProps } from '@mui/x-data-grid-pro'
import { type Dispatch, type FC, type SetStateAction } from 'react'
import { useLocation } from 'react-router-dom'
import {
    RosterSearchResultsTable,
    type RosterTableRowModel,
} from 'recruiter/components/RosterSearchResultsTable'
import { PeopleRosterSearchesToolbar } from 'recruiter/components/RosterTableToolbar'
import { onSearchesColumns, otherSearchesColumns } from './peopleRosterColumns'
import AddToRosterSqlPanel from './RosterPanelContent/AddToRosterSqlPanel'
import RosterSqlPanelContent from './RosterPanelContent/RosterSqlPanelContent'
import {
    GetFilteredRostersSansCandidate,
    GetFilteredRostersWithCandidate,
    QueryParamsProvider,
    RosterSearchProvider,
    RosterSqlSearchProvider,
} from 'recruiter/contexts'
import { toGraphQLRosterFilters } from 'shared/hooks/rosterSqlSearchUrlState'
import { type ApolloQueryRefetchFunction } from 'shared/types/graphql'
import { getPxAsRem } from 'shared/utils'
import { type CandidateRosterTableViewId } from '.'
import CandidateRosters from './CandidateRosters'
import Notes from './Notes'
import RecruitingPreferences from './RecruitingPreferences'

interface CandidateOverviewProps {
    data?: GetCandidateProfileForRecruiterQuery
    refetch: ApolloQueryRefetchFunction<
        GetCandidateProfileForRecruiterQuery,
        GetCandidateProfileForRecruiterQueryVariables
    >
    tableView: CandidateRosterTableViewId
    setTableView: Dispatch<SetStateAction<CandidateRosterTableViewId>>
}

const CandidateOverview: FC<CandidateOverviewProps> = ({
    data,
    refetch,
    tableView,
    setTableView,
}) => {
    const location = useLocation()
    const isOldView = location.pathname.includes('overview-old')
    const routeTableView: CandidateRosterTableViewId =
        location.pathname.endsWith('/all') ? 'all' : 'on'
    const candidateId = data?.candidate?.id

    const transformFilters = (
        filters: Parameters<typeof toGraphQLRosterFilters>[0]
    ) => ({
        ...toGraphQLRosterFilters(filters),
        type: 'Search',
    })

    const getDetailPanelContentOn: DataGridProProps<RosterTableRowModel>['getDetailPanelContent'] =
        (params) => <RosterSqlPanelContent data={params} />

    const getDetailPanelContentNotOn: DataGridProProps<RosterTableRowModel>['getDetailPanelContent'] =
        (params) => (
            <AddToRosterSqlPanel
                data={params}
                candidateId={candidateId ?? ''}
            />
        )

    const getDetailPanelHeight = () => 'auto' as const

    return (
        <Grid container spacing={getPxAsRem(24)}>
            <Grid item xs={12} md={8}>
                <Notes data={data} refetch={refetch} />
            </Grid>

            <Grid item xs={12} md={4}>
                <RecruitingPreferences />
            </Grid>

            {isOldView ? (
                <Grid item xs={12}>
                    <QueryParamsProvider>
                        <RosterSearchProvider
                            query={
                                tableView === 'on'
                                    ? GetFilteredRostersWithCandidate
                                    : GetFilteredRostersSansCandidate
                            }
                            candidateId={data?.candidate?.id ?? ''}
                            useSqlSearch
                        >
                            {data && (
                                <CandidateRosters
                                    data={data}
                                    tableView={tableView}
                                    setTableView={setTableView}
                                />
                            )}
                        </RosterSearchProvider>
                    </QueryParamsProvider>
                </Grid>
            ) : (
                <Grid
                    item
                    xs={12}
                    sx={{
                        height: '90vh',
                        display: 'flex',
                        overflow: 'hidden',
                    }}
                >
                    {routeTableView === 'on' ? (
                        <RosterSqlSearchProvider
                            candidateId={candidateId}
                            transformFilters={transformFilters}
                            isRowSelectable={(params) =>
                                (params.row as RosterTableRowModel).canEdit ??
                                false
                            }
                        >
                            <RosterSearchResultsTable
                                storageKey="people-rosters-on-columns"
                                columns={onSearchesColumns}
                                toolbar={<PeopleRosterSearchesToolbar />}
                                containerProps={{ elevation: 1 }}
                                checkboxSelection
                                getDetailPanelContent={getDetailPanelContentOn}
                                getDetailPanelHeight={getDetailPanelHeight}
                            />
                        </RosterSqlSearchProvider>
                    ) : (
                        <RosterSqlSearchProvider
                            enrichmentCandidateId={candidateId}
                            transformFilters={transformFilters}
                            isRowSelectable={(params) =>
                                (params.row as RosterTableRowModel).canEdit ??
                                false
                            }
                        >
                            <RosterSearchResultsTable
                                storageKey="people-rosters-not-on-columns"
                                columns={otherSearchesColumns(
                                    candidateId ?? ''
                                )}
                                toolbar={<PeopleRosterSearchesToolbar />}
                                containerProps={{ elevation: 1 }}
                                checkboxSelection
                                getDetailPanelContent={(params) =>
                                    (params.row as RosterTableRowModel)
                                        .rosterCandidateId
                                        ? getDetailPanelContentOn(params)
                                        : getDetailPanelContentNotOn(params)
                                }
                                getDetailPanelHeight={getDetailPanelHeight}
                            />
                        </RosterSqlSearchProvider>
                    )}
                </Grid>
            )}
        </Grid>
    )
}

export default CandidateOverview
